/** Called when the server returns 429 (rate limit). Argument: seconds to wait before retry. */
let onRateLimited: ((retryAfterSeconds: number) => void) | null = null;

export function setOnRateLimited(fn: ((retryAfterSeconds: number) => void) | null): void {
  onRateLimited = fn;
}

/** Called when the server returns 413 (request body too large). */
let onEventTooLarge: (() => void) | null = null;

export function setOnEventTooLarge(fn: (() => void) | null): void {
  onEventTooLarge = fn;
}

const MAX_RETRIES_ON_RATE_LIMIT = 2;

export function putEvent(
  slug: string,
  event: { type: string; message: string; data?: unknown },
): Promise<Response> {
  const url = `/c/${slug}/events`;
  const options: RequestInit = {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
  };

  let lastRes: Response | null = null;
  const run = async (): Promise<Response> => {
    for (let i = 0; i <= MAX_RETRIES_ON_RATE_LIMIT; i++) {
      lastRes = await fetch(url, options);
      if (lastRes.status === 413) {
        onEventTooLarge?.();
        return lastRes;
      }
      if (lastRes.status !== 429) return lastRes;
      const retryAfter = Math.min(
        Math.max(parseInt(lastRes.headers.get("Retry-After") ?? "5", 10) || 5, 2),
        60
      );
      onRateLimited?.(retryAfter);
      await new Promise((r) => setTimeout(r, retryAfter * 1000));
    }
    return lastRes!;
  };
  return run();
}

/** Mark events up to and including this event_id as "read" for unread count on the welcome screen. */
export function markRead(slug: string, eventId: number): Promise<Response> {
  return fetch(`/c/${slug}/mark-read`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event_id: eventId }),
    credentials: "same-origin",
  });
}

export interface AvatarCropPayload {
  /**
   * Crop rectangle in source image pixel coordinates.
   * (x, y) is the top-left corner, size is the square edge length.
   */
  x: number;
  y: number;
  size: number;
  /**
   * Optional scaling factor applied in the UI; sent so the server
   * can interpret crop coordinates if needed.
   */
  scale?: number;
}

export function uploadAvatar(
  slug: string,
  image: File | Blob,
  crop: AvatarCropPayload,
) {
  const form = new FormData();
  form.append("image", image);
  form.append("x", String(crop.x));
  form.append("y", String(crop.y));
  form.append("size", String(crop.size));
  if (typeof crop.scale === "number") {
    form.append("scale", String(crop.scale));
  }

  return fetch(`/c/${slug}/face`, {
    method: "POST",
    body: form,
  });
}

